import { useEffect, useRef } from 'react';
import { getSpoofTokenExpiry, mintSpoofToken } from 'clients/spoofSession';
import { TOKEN_RENEWAL_LEAD_MS } from 'clients/userTokenRefresh';

/** Cadence used when the spoof token's own expiry cannot be read. */
export const FALLBACK_RENEWAL_MS = 10 * 60 * 1000;

type UseSpoofSessionRenewalArgs = Readonly<{
  /** Company being spoofed, or null when the user is in their own company. */
  companyId: string | null;
  /** Called when the token could no longer be renewed and the session is over. */
  onSessionEnd: () => void;
}>;

/**
 * Keeps a spoof session alive by re-minting the spoof token just before it expires.
 *
 * Core-api issues spoof tokens with a 20-minute life and no refresh token, so the only
 * way to stay switched into a company is to keep calling `loginAs`. Renewal is scheduled
 * from the token's real `exp` rather than a fixed interval, and repeated on tab focus:
 * a backgrounded tab or a sleeping machine never fires the timer, and without the
 * catch-up the next request would 401 and drop the admin back to the company list.
 *
 * Mounted once in `UserProvider` so it is not tied to any particular route or to the
 * spoof banner being on screen.
 */
const useSpoofSessionRenewal = ({ companyId, onSessionEnd }: UseSpoofSessionRenewalArgs): void => {
  const onSessionEndRef = useRef(onSessionEnd);

  useEffect(() => {
    onSessionEndRef.current = onSessionEnd;
  }, [onSessionEnd]);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let renewing = false;

    const msUntilRenewal = (): number => {
      const expiry = getSpoofTokenExpiry();
      if (!expiry) {
        return FALLBACK_RENEWAL_MS;
      }
      return Math.max(0, expiry.getTime() - Date.now() - TOKEN_RENEWAL_LEAD_MS);
    };

    const schedule = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (cancelled) {
        return;
      }
      timer = setTimeout(renew, msUntilRenewal());
    };

    const renew = async () => {
      if (cancelled || renewing) {
        return;
      }
      renewing = true;
      try {
        const token = await mintSpoofToken(companyId);
        if (cancelled) {
          return;
        }
        if (!token) {
          onSessionEndRef.current();
          return;
        }
        schedule();
      } finally {
        renewing = false;
      }
    };

    const catchUp = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (msUntilRenewal() > 0) {
        schedule();
        return;
      }
      renew();
    };

    schedule();
    document.addEventListener('visibilitychange', catchUp);
    window.addEventListener('focus', catchUp);

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      document.removeEventListener('visibilitychange', catchUp);
      window.removeEventListener('focus', catchUp);
    };
  }, [companyId]);
};

export default useSpoofSessionRenewal;
